import { Component } from '@theme/component';
import { getConsultationAPI } from './consultation-api.js';

/**
 * Consultation Selector Component
 * Handles time slot selection for phone consultation booking.
 *
 * Flow:
 * 1. On mount: fetch available slots from API
 * 2. Render date buttons
 * 3. On date select: show time slots for that date
 * 4. On time select: show phone input
 * 5. On valid phone: dispatch consultation:changed ready=true
 *
 * @typedef {object} ConsultationSelectorRefs
 * @property {HTMLElement} loadingIndicator
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} datesContainer
 * @property {HTMLElement} dateButtons
 * @property {HTMLElement} timesContainer
 * @property {HTMLElement} timeButtons
 * @property {HTMLElement} phoneContainer
 * @property {HTMLInputElement} phoneInput
 * @property {HTMLButtonElement} phoneConfirmButton
 * @property {HTMLElement} selectedSummary
 * @property {HTMLElement} selectedText
 * @property {HTMLButtonElement} changeButton
 * @property {HTMLElement} helperText
 *
 * @extends Component<ConsultationSelectorRefs>
 */
class ConsultationSelectorComponent extends Component {
  /** @type {import('./consultation-api.js').ConsultationAPI | null} */
  #api = null;

  /** @type {Array<{date: string, times: string[]}>} */
  #slots = [];

  /** @type {string | null} */
  #selectedDate = null;

  /** @type {string | null} */
  #selectedTime = null;

  /** @type {string} */
  #phone = '';

  /** @type {boolean} */
  #phoneConfirmed = false;

  /** @type {boolean} */
  #ready = false;

  /** @type {string} */
  #locale = 'cs';

  connectedCallback() {
    super.connectedCallback();

    const apiUrl = this.dataset.apiUrl;
    this.#locale = this.dataset.locale || 'cs';

    if (apiUrl) {
      this.#api = getConsultationAPI(apiUrl, this.#locale);
      this.#loadSlots();
    } else {
      this.#showError('Consultation API is not configured.');
    }

    // Phone input listener — enables/disables confirm button
    if (this.refs.phoneInput) {
      this.refs.phoneInput.addEventListener('input', this.#onPhoneInput);
      this.refs.phoneInput.addEventListener('keydown', this.#onPhoneKeydown);
    }

    // Phone confirm button listener
    if (this.refs.phoneConfirmButton) {
      this.refs.phoneConfirmButton.addEventListener('click', this.#onPhoneConfirm);
    }

    // Change button listener
    if (this.refs.changeButton) {
      this.refs.changeButton.addEventListener('click', this.#onChangeClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.phoneInput) {
      this.refs.phoneInput.removeEventListener('input', this.#onPhoneInput);
      this.refs.phoneInput.removeEventListener('keydown', this.#onPhoneKeydown);
    }
    if (this.refs.phoneConfirmButton) {
      this.refs.phoneConfirmButton.removeEventListener('click', this.#onPhoneConfirm);
    }
    if (this.refs.changeButton) {
      this.refs.changeButton.removeEventListener('click', this.#onChangeClick);
    }
  }

  /**
   * Load available slots from API.
   */
  async #loadSlots() {
    this.#showLoading(true);
    this.#hideError();

    try {
      const data = await this.#api.getSlots();
      this.#slots = data.slots || [];

      this.#showLoading(false);

      if (this.#slots.length === 0) {
        this.#showError(this.dataset.noSlotsText || 'No consultation slots available.');
        return;
      }

      this.#renderDates();
      this.refs.datesContainer.hidden = false;
    } catch (error) {
      this.#showLoading(false);
      this.#showError(this.dataset.loadErrorText || 'Failed to load available dates.');
      console.warn('[MEG] Failed to load consultation slots:', error);
    }
  }

  /**
   * Render date buttons.
   */
  #renderDates() {
    const { dateButtons } = this.refs;
    if (!dateButtons) return;

    dateButtons.innerHTML = '';

    for (const slot of this.#slots) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'consultation-selector__date-btn';
      btn.dataset.date = slot.date;

      const date = new Date(slot.date + 'T00:00:00');
      const dayName = date.toLocaleDateString(this.#locale, { weekday: 'short' });
      const dayNum = date.toLocaleDateString(this.#locale, { day: 'numeric', month: 'numeric' });

      btn.innerHTML = `
        <span class="consultation-selector__date-day">${dayName}</span>
        <span class="consultation-selector__date-label">${dayNum}</span>
      `;

      if (this.#selectedDate === slot.date) {
        btn.classList.add('consultation-selector__date-btn--active');
      }

      btn.addEventListener('click', () => this.#onDateSelect(slot.date));
      dateButtons.appendChild(btn);
    }
  }

  /**
   * Handle date selection.
   * @param {string} date
   */
  #onDateSelect(date) {
    this.#selectedDate = date;
    this.#selectedTime = null;

    // Update date button active state
    const buttons = this.refs.dateButtons?.querySelectorAll('.consultation-selector__date-btn');
    buttons?.forEach((btn) => {
      btn.classList.toggle(
        'consultation-selector__date-btn--active',
        /** @type {HTMLElement} */ (btn).dataset.date === date
      );
    });

    this.#renderTimes();
    this.refs.timesContainer.hidden = false;

    // Show phone input
    this.refs.phoneContainer.hidden = false;

    this.#updateReadyState();
  }

  /**
   * Render time slot buttons for the selected date.
   */
  #renderTimes() {
    const { timeButtons } = this.refs;
    if (!timeButtons || !this.#selectedDate) return;

    timeButtons.innerHTML = '';

    const slot = this.#slots.find((s) => s.date === this.#selectedDate);
    if (!slot) return;

    for (const time of slot.times) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'consultation-selector__time-btn';
      btn.dataset.time = time;
      btn.textContent = time;

      if (this.#selectedTime === time) {
        btn.classList.add('consultation-selector__time-btn--active');
      }

      btn.addEventListener('click', () => this.#onTimeSelect(time));
      timeButtons.appendChild(btn);
    }
  }

  /**
   * Handle time selection.
   * @param {string} time
   */
  #onTimeSelect(time) {
    this.#selectedTime = time;

    // Update time button active state
    const buttons = this.refs.timeButtons?.querySelectorAll('.consultation-selector__time-btn');
    buttons?.forEach((btn) => {
      btn.classList.toggle(
        'consultation-selector__time-btn--active',
        /** @type {HTMLElement} */ (btn).dataset.time === time
      );
    });

    // Focus phone input if empty
    if (this.refs.phoneInput && !this.refs.phoneInput.value.trim()) {
      this.refs.phoneInput.focus();
    }

    this.#updateReadyState();
  }

  /**
   * Handle phone input — enable/disable confirm button.
   */
  #onPhoneInput = () => {
    this.#phone = this.refs.phoneInput?.value.trim() || '';
    if (this.refs.phoneConfirmButton) {
      this.refs.phoneConfirmButton.disabled = !this.#isPhoneValid();
    }
  };

  /**
   * Handle Enter key in phone input — confirm if valid.
   * @param {KeyboardEvent} e
   */
  #onPhoneKeydown = (e) => {
    if (e.key === 'Enter' && this.#isPhoneValid()) {
      e.preventDefault();
      this.#onPhoneConfirm();
    }
  };

  /**
   * Handle phone confirm button click.
   */
  #onPhoneConfirm = () => {
    if (!this.#isPhoneValid()) return;
    this.#phone = this.refs.phoneInput?.value.trim() || '';
    this.#phoneConfirmed = true;
    this.#updateReadyState();
  };

  /**
   * Handle change button click — reset selection.
   */
  #onChangeClick = () => {
    this.#selectedDate = null;
    this.#selectedTime = null;
    this.#phoneConfirmed = false;
    this.#ready = false;

    // Hide summary, show selection UI
    this.refs.selectedSummary.hidden = true;
    this.refs.datesContainer.hidden = false;
    this.refs.timesContainer.hidden = true;
    this.refs.phoneContainer.hidden = true;

    // Reset date button state
    this.#renderDates();

    this.#dispatchChanged();
  };

  /**
   * Update ready state and show/hide summary.
   */
  #updateReadyState() {
    const wasReady = this.#ready;
    this.#ready = this.#selectedDate !== null && this.#selectedTime !== null && this.#phoneConfirmed && this.#isPhoneValid();

    if (this.#ready && !wasReady) {
      // Show summary
      this.#showSummary();
    } else if (!this.#ready) {
      this.refs.selectedSummary.hidden = true;
    }

    this.#dispatchChanged();
  }

  /**
   * Show the selected consultation summary.
   */
  #showSummary() {
    const { selectedSummary, selectedText } = this.refs;
    if (!selectedSummary || !selectedText) return;

    const displayName = this.#getDisplayName();
    selectedText.textContent = displayName;
    selectedSummary.hidden = false;
  }

  /**
   * Format the selected date/time for display.
   * @returns {string}
   */
  #getDisplayName() {
    if (!this.#selectedDate || !this.#selectedTime) return '';

    const date = new Date(this.#selectedDate + 'T00:00:00');
    const formatted = date.toLocaleDateString(this.#locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });

    return `${formatted}, ${this.#selectedTime}`;
  }

  /**
   * Validate phone number.
   * Allowed formats: +XXX XXX XXX XXX, +XXXXXXXXXXXX, XXX XXX XXX, XXXXXXXXX
   * @returns {boolean}
   */
  #isPhoneValid() {
    const phone = this.refs.phoneInput?.value.trim() || '';
    return /^(\+\d{3}\s?\d{3}\s?\d{3}\s?\d{3}|\d{3}\s?\d{3}\s?\d{3})$/.test(phone);
  }

  /**
   * Dispatch consultation:changed event.
   */
  #dispatchChanged() {
    this.dispatchEvent(
      new CustomEvent('consultation:changed', {
        bubbles: true,
        detail: {
          ready: this.#ready,
          data: this.#ready
            ? {
                date: this.#selectedDate,
                time: this.#selectedTime,
                phone: this.refs.phoneInput?.value.trim() || '',
              }
            : null,
        },
      })
    );
  }

  // --- Public API ---

  /**
   * Get current consultation data.
   * @returns {{ date: string, time: string, phone: string, displayName: string } | null}
   */
  getConsultation() {
    if (!this.#ready) return null;

    return {
      date: this.#selectedDate,
      time: this.#selectedTime,
      phone: this.refs.phoneInput?.value.trim() || '',
      displayName: this.#getDisplayName(),
    };
  }

  /**
   * Check if the consultation selection is valid.
   * @returns {boolean}
   */
  isValid() {
    return this.#ready;
  }

  /**
   * Create a soft-lock reservation via the API.
   * @returns {Promise<{token: string, date: string, time: string, phone: string, displayName: string, expires_at: string} | null>}
   */
  async createReservation() {
    if (!this.#ready || !this.#api) return null;

    const phone = this.refs.phoneInput?.value.trim() || '';
    const email = this.dataset.customerEmail || '';

    try {
      const result = await this.#api.createReservation({
        date: this.#selectedDate,
        time: this.#selectedTime,
        phone,
        email: email || undefined,
      });

      return {
        token: result.token,
        date: result.date,
        time: result.time,
        phone,
        displayName: this.#getDisplayName(),
        expires_at: result.expires_at,
      };
    } catch (error) {
      console.warn('[MEG] Failed to create consultation reservation:', error);
      throw error;
    }
  }

  // --- UI helpers ---

  /**
   * @param {boolean} show
   */
  #showLoading(show) {
    const { loadingIndicator } = this.refs;
    if (loadingIndicator) loadingIndicator.hidden = !show;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }
  }

  #hideError() {
    const { errorMessage } = this.refs;
    if (errorMessage) errorMessage.hidden = true;
  }
}

if (!customElements.get('consultation-selector-component')) {
  customElements.define('consultation-selector-component', ConsultationSelectorComponent);
}

export { ConsultationSelectorComponent };
